/**
 * 오픈오피스 절차 텍스처 (외부 에셋 없이 CanvasTexture ≤1024).
 * 밝은 오크 플랭크 플로어 / 창밖 하늘+도시 고층빌딩 실루엣을 캔버스에 직접 그린다.
 * 모든 컬러 텍스처는 SRGBColorSpace를 지정한다.
 */
import * as THREE from 'three';
import { makeCanvas, toTexture } from './canvas2d';
import { mulberry32 } from './rng';

// 월넛 텍스처는 책상 상판(SceneManager)과 배경 데스크(office)가 공유 — 1회 생성 캐시.
let cachedWalnut: THREE.CanvasTexture | null = null;

/**
 * 월넛 원목 슬랩 (레퍼런스 책상 톤): 저채도 다크 브라운 베이스 + 가늘고 불규칙한
 * 다크 스트릭 + 완만한 캐서드럴 결. 붉은 마호가니/와이드 플랭크 느낌을 배제한다.
 */
export function makeWalnutTexture(): THREE.CanvasTexture {
  if (cachedWalnut) return cachedWalnut;
  const W = 1024;
  const H = 512;
  const { canvas, ctx } = makeCanvas(W, H);
  const rand = mulberry32(45123);

  // 베이스: 저채도 월넛(#5a4433 계열).
  ctx.fillStyle = '#5a4433';
  ctx.fillRect(0, 0, W, H);

  // 완만한 수평 명도 밴드(원목 슬랩의 톤 변화 — 플랭크 경계선 없음).
  for (let i = 0; i < 26; i++) {
    const y = rand() * H;
    const bh = 14 + rand() * 64;
    ctx.fillStyle = `hsla(${22 + rand() * 8}, ${22 + rand() * 8}%, ${25 + rand() * 10}%, ${0.1 + rand() * 0.14})`;
    ctx.fillRect(0, y - bh / 2, W, bh);
  }

  // 가늘고 불규칙한 다크 스트릭(수평 물결 라인 — 월넛 특유의 결).
  for (let s = 0; s < 150; s++) {
    const y0 = rand() * H;
    const dark = rand() < 0.78;
    ctx.strokeStyle = dark
      ? `rgba(38, 26, 17, ${0.1 + rand() * 0.16})`
      : `rgba(148, 116, 86, ${0.05 + rand() * 0.09})`;
    ctx.lineWidth = 0.6 + rand() * 1.3;
    ctx.beginPath();
    const amp = 1.5 + rand() * 5;
    const seg = 8 + Math.floor(rand() * 5);
    ctx.moveTo(0, y0);
    for (let i = 1; i <= seg; i++) {
      ctx.lineTo((W / seg) * i, y0 + Math.sin(i * (1.3 + rand())) * amp);
    }
    ctx.stroke();
  }

  // 캐서드럴(길게 늘어진 동심 아치 결) 몇 곳.
  for (let a = 0; a < 5; a++) {
    const cx = rand() * W;
    const cy = rand() * H;
    for (let r = 12; r < 90; r += 9 + rand() * 8) {
      ctx.strokeStyle = `rgba(44, 30, 19, ${0.07 + rand() * 0.08})`;
      ctx.lineWidth = 1 + rand() * 1.2;
      ctx.beginPath();
      ctx.ellipse(cx, cy, r * 2.7, r * 0.75, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
  }

  // 미세 스펙클(균일함 제거).
  for (let i = 0; i < 3000; i++) {
    ctx.fillStyle = rand() < 0.6
      ? `rgba(30, 20, 12, ${0.05 + rand() * 0.06})`
      : `rgba(158, 128, 98, ${0.04 + rand() * 0.05})`;
    ctx.fillRect(rand() * W, rand() * H, 1 + rand() * 2, 1);
  }

  const tex = toTexture(canvas, { wrap: THREE.RepeatWrapping, aniso: 8 });
  cachedWalnut = tex;
  return tex;
}

/**
 * 밝은 오크 우드 플로어 (세로 방향 플랭크 + 엇갈린 이음매 + 결 스트릭).
 * 타일 1장 ≈ 실측 1.8m×1.8m 가정으로 repeat를 잡는다.
 */
export function makeOakFloorTexture(): THREE.CanvasTexture {
  const S = 1024;
  const { canvas, ctx } = makeCanvas(S, S);
  const rand = mulberry32(20260711);

  const cols = 7;
  const plankW = S / cols;

  // 바탕(플랭크 사이 미세 틈 컬러).
  ctx.fillStyle = '#a98a62';
  ctx.fillRect(0, 0, S, S);

  for (let c = 0; c < cols; c++) {
    const x0 = c * plankW;
    // 열마다 이음매를 엇갈리게(타일러블: 오프셋 후 세그먼트가 S를 넘으면 감아 붙임).
    // 오프셋을 크게 랜덤화해 이음선 반복 패턴이 눈에 띄지 않게 한다.
    const offset = (c * 0.37 + rand() * 0.55) * S;
    // 열(플랭크)별 명도 지터 — 널빤지마다 톤이 달라 타일 반복감을 줄인다.
    const colLit = (rand() - 0.5) * 9;
    let y = -offset;
    while (y < S) {
      // 길쭉한 플랭크(폭 대비 4~6배 길이)로 '긴 마루널' 느낌을 만든다.
      const segH = S * (0.55 + rand() * 0.35);
      const yy = y;
      // 플랭크 바탕색: 꿀색 오크 톤(0xd8b98a 근처)에 랜덤 변주.
      const hue = 34 + (rand() - 0.5) * 8;
      const sat = 46 + (rand() - 0.5) * 12;
      const lit = 58 + colLit + (rand() - 0.5) * 9;
      ctx.fillStyle = `hsl(${hue}, ${sat}%, ${lit}%)`;
      ctx.fillRect(x0 + 1, yy + 1, plankW - 2, segH - 2);

      // 나뭇결 스트릭(세로 방향, 낮은 알파의 물결 라인).
      const streaks = 26;
      for (let s = 0; s < streaks; s++) {
        const sx = x0 + 3 + rand() * (plankW - 6);
        const dark = rand() < 0.7;
        ctx.strokeStyle = dark
          ? `rgba(112, 78, 42, ${0.05 + rand() * 0.08})`
          : `rgba(255, 238, 210, ${0.05 + rand() * 0.06})`;
        ctx.lineWidth = 0.8 + rand() * 1.6;
        ctx.beginPath();
        const wob = 2 + rand() * 5;
        ctx.moveTo(sx, yy);
        for (let t = 0; t <= 1.001; t += 0.2) {
          ctx.lineTo(sx + Math.sin((t * 6 + rand()) * 1.7) * wob, yy + segH * t);
        }
        ctx.stroke();
      }

      // 옹이(가끔).
      if (rand() < 0.3) {
        const kx = x0 + plankW * (0.25 + rand() * 0.5);
        const ky = yy + segH * (0.2 + rand() * 0.6);
        const kr = 4 + rand() * 8;
        const g = ctx.createRadialGradient(kx, ky, 0, kx, ky, kr);
        g.addColorStop(0, 'rgba(96, 64, 34, 0.55)');
        g.addColorStop(0.6, 'rgba(120, 84, 48, 0.25)');
        g.addColorStop(1, 'rgba(120, 84, 48, 0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.ellipse(kx, ky, kr, kr * 1.6, 0, 0, Math.PI * 2);
        ctx.fill();
      }

      // 이음매(가로 라인).
      ctx.fillStyle = 'rgba(90, 62, 34, 0.5)';
      ctx.fillRect(x0, ((yy % S) + S) % S, plankW, 2);
      y += segH;
    }
    // 플랭크 세로 경계선.
    ctx.fillStyle = 'rgba(90, 62, 34, 0.45)';
    ctx.fillRect(x0, 0, 2, S);
  }

  // 저알파 노이즈 스펙클로 균일함 제거.
  for (let i = 0; i < 5000; i++) {
    const v = rand();
    ctx.fillStyle = v < 0.5 ? `rgba(70, 48, 26, ${0.03 + rand() * 0.05})` : `rgba(255, 244, 224, ${0.03 + rand() * 0.04})`;
    ctx.fillRect(rand() * S, rand() * S, 1 + rand() * 2, 1 + rand() * 2);
  }

  return toTexture(canvas, { wrap: THREE.RepeatWrapping, aniso: 8 });
}

/**
 * 창밖 뷰: 밝은 하늘 그라데이션 + 옅은 태양 헤이즈 + 도시 고층빌딩 실루엣 2겹.
 * 창 유리면 뒤에 붙이는 발광성 배경판용.
 */
export function makeCityWindowTexture(): THREE.CanvasTexture {
  const W = 1024;
  const H = 512;
  const { canvas, ctx } = makeCanvas(W, H);
  const rand = mulberry32(9137);
  const horizon = H * 0.62;

  // 하늘: 위(청량한 블루) → 지평선(거의 백색 헤이즈). 상단 채도를 올려 창이 읽히게.
  const sky = ctx.createLinearGradient(0, 0, 0, horizon);
  sky.addColorStop(0, '#93bfe4');
  sky.addColorStop(0.65, '#c9e0f0');
  sky.addColorStop(1, '#eaf4fa');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, W, horizon);

  // 태양 헤이즈(우상단, 은은한 광).
  const sun = ctx.createRadialGradient(W * 0.74, H * 0.2, 10, W * 0.74, H * 0.2, H * 0.5);
  sun.addColorStop(0, 'rgba(255, 252, 240, 0.9)');
  sun.addColorStop(0.4, 'rgba(255, 250, 235, 0.28)');
  sun.addColorStop(1, 'rgba(255, 250, 235, 0)');
  ctx.fillStyle = sun;
  ctx.fillRect(0, 0, W, horizon);

  // 지평선 아래(도시 저층부 헤이즈).
  const base = ctx.createLinearGradient(0, horizon, 0, H);
  base.addColorStop(0, '#c2d1dc');
  base.addColorStop(1, '#aebfcc');
  ctx.fillStyle = base;
  ctx.fillRect(0, horizon, W, H - horizon);

  // 원경 빌딩(옅은 블루그레이, 디테일 없음).
  ctx.fillStyle = '#b9cbd9';
  let x = -20;
  while (x < W + 20) {
    const bw = 34 + rand() * 60;
    const bh = 30 + rand() * 130;
    ctx.fillRect(x, horizon - bh, bw, bh + 8);
    x += bw + rand() * 26;
  }

  // 근경 빌딩(진한 블루그레이 + 창문 스펙) — 하늘 대비를 강화해 실루엣이 읽히게.
  x = -30;
  while (x < W + 30) {
    const bw = 46 + rand() * 78;
    const bh = 60 + rand() * 230;
    const top = horizon - bh;
    ctx.fillStyle = `hsl(${208 + rand() * 12}, ${20 + rand() * 8}%, ${42 + rand() * 10}%)`;
    ctx.fillRect(x, top, bw, bh + 30);
    // 창문 그리드(라이트/다크 랜덤).
    const cw = 5;
    const ch = 7;
    for (let wy = top + 8; wy < horizon + 16; wy += ch + 4) {
      for (let wx = x + 5; wx < x + bw - 6; wx += cw + 4) {
        const v = rand();
        if (v < 0.42) continue; // 일부는 벽면 그대로
        ctx.fillStyle = v < 0.8 ? 'rgba(240, 248, 255, 0.85)' : 'rgba(66, 82, 100, 0.6)';
        ctx.fillRect(wx, wy, cw, ch);
      }
    }
    x += bw + 6 + rand() * 30;
  }

  // 전체를 살짝 밝은 헤이즈로 덮어 거리감(창 유리 특유의 뿌연 톤).
  ctx.fillStyle = 'rgba(240, 247, 252, 0.07)';
  ctx.fillRect(0, 0, W, H);

  return toTexture(canvas, { wrap: THREE.ClampToEdgeWrapping, aniso: 4 });
}
